/**
 * HTML de impresión (PDF vía "Guardar como PDF") con cabecera y pie
 * alineados con Actuación / Formación.
 */

const MESES_ES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

const LABEL_ESTADO_ENSAYO = {
  PROGRAMADO: 'Programados', CANCELADO: 'Cancelados', REALIZADO: 'Realizados',
};
const LABEL_ESTADO_ASIS = {
  PENDIENTE: 'Pendiente', PRESENTE: 'Presente', AUSENTE: 'Ausente', JUSTIFICADO: 'Justificado',
};
const LABEL_ESTADO_ACTUACION = {
  BORRADOR: 'Borrador', ENCUESTA_ABIERTA: 'Encuesta abierta', EN_PREPARACION: 'En preparación',
  LISTA: 'Lista', ARCHIVADA: 'Archivada',
};
const LABEL_AVAL_STATUS = {
  PENDING: 'Pendiente de aceptar', ACCEPTED: 'Aceptado', REJECTED: 'Rechazado',
};
const LABEL_DELTA_KPI = {
  ensayosTotal: 'Ensayos (total)', horasSuma: 'Horas de ensayo (con hora de fin)',
  actuacionesTotal: 'Actuaciones (con fecha)', presenteRegistros: 'Asist. ensayo «Presente»',
  obrasCreadas: 'Obras creadas (archivo)', paginasCreadas: 'Páginas creadas (archivo)',
  resenasRecibidas: 'Reseñas recibidas (fecha de creación)',
};

const PIE_NEUTRAL = ['#805AD5', '#2C7A7B', '#3182CE', '#D69E2E', '#E53E3E', '#4FD1C5', '#9F7AEA', '#ED8936'];
const HEX_EST_ENSAYO = { PROGRAMADO: '#805AD5', CANCELADO: '#E53E3E', REALIZADO: '#38A169' };
const HEX_EST_ASIS = {
  PENDIENTE: '#A0AEC0', PRESENTE: '#38A169', AUSENTE: '#F56565', JUSTIFICADO: '#ED8936',
};
const HEX_EST_ACT = {
  BORRADOR: '#718096', ENCUESTA_ABIERTA: '#4FD1C5', EN_PREPARACION: '#B794F4',
  LISTA: '#2B6CB0', ARCHIVADA: '#805AD5',
};
const HEX_AVAL = { PENDING: '#D69E2E', ACCEPTED: '#38A169', REJECTED: '#E53E3E' };

function esc(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatYmdEs(ymd) {
  if (!ymd || typeof ymd !== 'string') return '—';
  const p = ymd.split('-');
  if (p.length !== 3) return esc(ymd);
  const y = parseInt(p[0], 10);
  const m = parseInt(p[1], 10);
  const d = parseInt(p[2], 10);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return esc(ymd);
  return new Date(y, m - 1, d).toLocaleDateString('es-ES', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function tituloPeriodo(modoSnap, data, t) {
  if (!data) {
    return { linea1: t.informe.tituloRango, linea2: t.informe.sinRango };
  }
  const d1 = data.desdeYmd ? formatYmdEs(data.desdeYmd) : null;
  const d2 = data.hastaYmd ? formatYmdEs(data.hastaYmd) : null;
  if (modoSnap?.tipo === 'anio' && typeof modoSnap.anio === 'number') {
    return { linea1: t.informe.tituloAnio(modoSnap.anio), linea2: null };
  }
  if (modoSnap?.tipo === 'mes' && modoSnap.mes && modoSnap.anioMes) {
    const mnom = MESES_ES[(Number(modoSnap.mes) || 1) - 1] || '';
    return { linea1: t.informe.tituloMes(mnom, modoSnap.anioMes), linea2: null };
  }
  if (d1 && d2) {
    return { linea1: t.informe.tituloRango, linea2: t.informe.entreFechas(d1, d2) };
  }
  return { linea1: t.informe.tituloRango, linea2: null };
}

function formatPct(f) {
  if (f == null || Number.isNaN(f)) return '—';
  return `${(f * 100).toFixed(1)}%`;
}

function formatDeltaLine(d) {
  if (!d) return '—';
  const sign = d.diff > 0 ? '+' : '';
  const rel =
    d.diffRel != null && !Number.isNaN(d.diffRel)
      ? ` (${sign}${(d.diffRel * 100).toFixed(1)}% vs ant.)`
      : '';
  return `${sign}${d.diff}${rel}`;
}

function nombreMusico(n, a) {
  return [n, a].filter(Boolean).join(' ').trim() || '—';
}

function toPieDataWithKeyColors(record, labelMap, hexByKey) {
  return Object.entries(record || {})
    .filter(([, n]) => n > 0)
    .map(([k, v], i) => ({
      name: labelMap[k] || k,
      value: v,
      key: k,
      color: hexByKey[k] || PIE_NEUTRAL[i % PIE_NEUTRAL.length],
    }));
}

function toPieDataGeneric(record, labelMap) {
  return Object.entries(record || {})
    .filter(([, n]) => n > 0)
    .map(([k, v], i) => ({
      name: labelMap[k] || k,
      value: v,
      color: PIE_NEUTRAL[i % PIE_NEUTRAL.length],
    }));
}

function svgPieFromItems(items, size = 150) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.38;
  const total = items.reduce((s, d) => s + d.value, 0);
  if (total <= 0) {
    return `<p style="color:#718096;font-size:11px">Sin datos</p>`;
  }
  let angle = -Math.PI / 2;
  const parts = [];
  for (const d of items) {
    const slice = (d.value / total) * 2 * Math.PI;
    const x1 = cx + r * Math.cos(angle);
    const y1 = cy + r * Math.sin(angle);
    const a2 = angle + slice;
    const x2 = cx + r * Math.cos(a2);
    const y2 = cy + r * Math.sin(a2);
    const large = slice > Math.PI ? 1 : 0;
    const pathD = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
    parts.push(`<path d="${pathD}" fill="${d.color}" stroke="#fff" stroke-width="0.5"/>`);
    angle = a2;
  }
  const legend = items
    .map(
      (d) => `<tr><td style="padding:2px 8px 2px 0;vertical-align:middle">
        <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${d.color}"></span>
      </td><td style="font-size:11px;padding:2px 0">${esc(d.name)}</td>
      <td style="text-align:right;font-size:11px;font-weight:700;padding-left:8px">${d.value}</td></tr>`,
    )
    .join('');
  return `<div style="display:inline-block;vertical-align:top">
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">${parts.join('')}</svg>
  </div>
  <table style="display:inline-block;vertical-align:top;margin-left:12px;border-collapse:collapse">
    <tbody>${legend}</tbody>
  </table>`;
}

function hBarRow(name, n, max, fill = '#805AD5') {
  const pct = max > 0 ? Math.round((n / max) * 100) : 0;
  return `<div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;font-size:11px">
    <div style="width:120px;flex-shrink:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${esc(name)}">${esc(name)}</div>
    <div style="flex:1;min-width:60px;height:8px;background:#EDE9FE;border-radius:4px;overflow:hidden">
      <div style="width:${pct}%;height:100%;background:${fill};border-radius:4px"></div>
    </div>
    <div style="width:32px;text-align:right;font-weight:700">${n}</div>
  </div>`;
}

function hBarsFromNamed(items, keyName, valKey, maxN = 10) {
  const list = (items || []).slice(0, maxN);
  const max = Math.max(1, ...list.map((x) => x[valKey] || 0));
  return list.map((x) => hBarRow(x[keyName] || '—', x[valKey] || 0, max)).join('');
}

function sectionTitle(x) {
  return `<div class="section-title">${x}</div>`;
}

function tdContent(c) {
  if (c != null && typeof c === 'object' && 'html' in c) {
    const right = c.numeric ? ' style="text-align:right"' : '';
    return `<td${right}>${c.html}</td>`;
  }
  if (c != null && typeof c === 'object' && 'text' in c) {
    return `<td${c.numeric ? ' style="text-align:right"' : ''}>${esc(c.text)}</td>`;
  }
  return `<td>${esc(c)}</td>`;
}

function simpleTable(headers, bodyRows) {
  const th = headers.map((h) => `<th>${esc(h)}</th>`).join('');
  const tr = bodyRows
    .map((cells) => `<tr>${cells.map((c) => tdContent(c)).join('')}</tr>`)
    .join('');
  return `<table><thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table>`;
}

async function fetchImgDataUrl(origin, path) {
  try {
    const res = await fetch(`${origin}${path}`);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onloadend = () => resolve(String(fr.result || ''));
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function buildResumenMain(data, withCharts) {
  const r = data.resumen;
  if (!r) return '';
  const out = [];
  out.push(sectionTitle('Resumen de indicadores'));
  out.push(
    simpleTable(
      ['Concepto', 'Dato'],
      [
        [
          'Ensayos (total con fecha en el rango)',
          { html: String(r.ensayos?.total ?? '—') },
        ],
        [
          'Horas de ensayo (con hora de fin)',
          { html: `${r.ensayos?.horasSuma ?? '—'} h` },
        ],
        [
          'Registros de asistencia (músico × ensayo)',
          { html: String(r.asistenciaEnsayos?.totalRegistros ?? '—') },
        ],
        [
          'Tasa presente / cerrado (asist. ensayos)',
          { html: formatPct(r.asistenciaEnsayos?.tasaPresenteSobreCerrado) },
        ],
        [
          'Actuaciones (total con fecha en el rango)',
          { html: String(r.actuaciones?.total ?? '—') },
        ],
      ],
    ),
  );

  const subE = toPieDataWithKeyColors(r.ensayos?.porEstado, LABEL_ESTADO_ENSAYO, HEX_EST_ENSAYO);
  const subA = toPieDataWithKeyColors(
    r.asistenciaEnsayos?.porEstado,
    LABEL_ESTADO_ASIS,
    HEX_EST_ASIS,
  );
  const subAct = toPieDataWithKeyColors(
    r.actuaciones?.porEstado,
    LABEL_ESTADO_ACTUACION,
    HEX_EST_ACT,
  );

  if (withCharts) {
    out.push(
      '<div class="print-grid-2" style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:12px">',
    );
    out.push(
      `<div class="ic"><div class="ic-label">Por estado — Ensayos</div>${svgPieFromItems(
        subE.map(({ name, value, color }) => ({ name, value, color })),
      )}</div>`,
    );
    out.push(
      `<div class="ic"><div class="ic-label">Por estado — Asistencia a ensayos</div>${svgPieFromItems(
        subA.map(({ name, value, color }) => ({ name, value, color })),
      )}</div>`,
    );
    out.push('</div>');
    out.push(
      `<div class="ic" style="margin-top:12px"><div class="ic-label">Por estado — Actuaciones</div>${svgPieFromItems(
        subAct.map(({ name, value, color }) => ({ name, value, color })),
      )}</div>`,
    );
  } else {
    out.push(
      simpleTable(
        ['Estado ensayo', 'Nº'],
        Object.entries(r.ensayos?.porEstado || {}).map(([k, v]) => [LABEL_ESTADO_ENSAYO[k] || k, { html: String(v), numeric: true }]),
      ),
    );
    out.push(
      simpleTable(
        ['Estado asistencia', 'Nº'],
        Object.entries(r.asistenciaEnsayos?.porEstado || {}).map(
          ([k, v]) => [LABEL_ESTADO_ASIS[k] || k, { html: String(v), numeric: true }],
        ),
      ),
    );
    out.push(
      simpleTable(
        ['Estado actuación', 'Nº'],
        Object.entries(r.actuaciones?.porEstado || {}).map(
          ([k, v]) => [LABEL_ESTADO_ACTUACION[k] || k, { html: String(v), numeric: true }],
        ),
      ),
    );
  }
  return out.join('');
}

function buildRepertorio(ra, withCharts) {
  if (!ra) return '';
  const out = [sectionTitle('Repertorio en actuaciones')];
  out.push(
    `<p style="font-size:12px;color:#553C9A;margin-bottom:8px">Actuaciones analizadas: <strong>${ra.actuacionesAnalizadas ?? '—'}</strong></p>`,
  );
  if (withCharts) {
    out.push(
      `<div class="ic"><div class="ic-label">Obras (top 10)</div><div style="padding:8px 0">${
        hBarsFromNamed(ra.obras, 'titulo', 'apariciones', 10) || '<p style="font-size:11px">—</p>'
      }</div></div>`,
    );
    out.push(
      `<div class="ic" style="margin-top:8px"><div class="ic-label">Autores (top 10)</div><div style="padding:8px 0">${
        hBarsFromNamed(ra.autores, 'autor', 'apariciones', 10) || '<p style="font-size:11px">—</p>'
      }</div></div>`,
    );
  } else {
    out.push(
      simpleTable(
        ['Obra', 'Autor', 'Apar.'],
        (ra.obras || []).map((o) => [o.titulo, o.autor || '—', { html: String(o.apariciones ?? '—'), numeric: true }]),
      ),
    );
    out.push(
      simpleTable(
        ['Autor', 'Apar. en obras'],
        (ra.autores || []).map((a) => [a.autor, { html: String(a.apariciones ?? '—'), numeric: true }]),
      ),
    );
  }
  return out.join('');
}

function buildEncuestasActuacion(r, withCharts) {
  const e = r.encuestasAsistenciaActuaciones;
  if (!e) return '';
  const out = [sectionTitle('Encuestas (asistencia a actuación)')];
  out.push(
    simpleTable(
      ['Concepto', 'Valor'],
      [
        [
          'Actuaciones con encuesta en el rango',
          { html: String(e.actuacionesConEncuesta ?? '—') },
        ],
        [
          'Total respuestas en el periodo',
          { html: String(e.totalRespuestas ?? '—') },
        ],
        [
          'Tasa «sí / sí+no»',
          { html: formatPct(e.tasaSobreRespuestaBinaria) },
        ],
      ],
    ),
  );
  if (withCharts) {
    const pieItems = toPieDataGeneric(
      { 'Sí': e.asisteSi, 'No': e.asisteNo, 'Sin contestar': e.sinContestar },
      { 'Sí': 'Sí', 'No': 'No', 'Sin contestar': 'Sin contestar' },
    );
    out.push(
      `<div class="ic" style="margin-top:8px"><div class="ic-label">Distribución de respuestas</div>${svgPieFromItems(pieItems)}</div>`,
    );
  } else {
    out.push(
      simpleTable(
        ['Respuesta', 'Nº'],
        [
          ['Sí', { html: String(e.asisteSi ?? 0), numeric: true }],
          ['No', { html: String(e.asisteNo ?? 0), numeric: true }],
          ['Sin contestar', { html: String(e.sinContestar ?? 0), numeric: true }],
        ],
      ),
    );
  }
  if (r.pulidoFase7?.encuestasTasaRespuesta) {
    const er = r.pulidoFase7.encuestasTasaRespuesta;
    out.push(
      `<p style="font-size:11px;margin-top:8px;color:#4A5568">Tasa de respuesta (invitaciones): ${
        er.tasa != null ? formatPct(er.tasa) : '—'
      } · ${er.conRespuesta} / ${er.invitaciones}</p>`,
    );
  }
  return out.join('');
}

function buildArchivoPlantilla(r) {
  const bits = [];
  if (r.archivoObrasPartituras) {
    const a = r.archivoObrasPartituras;
    bits.push(sectionTitle('Archivo de partituras'));
    bits.push(
      simpleTable(
        ['Indicador', 'Valor'],
        [
          ['Obras creadas en el periodo', { html: String(a.obrasCreadas ?? '—') }],
          ['Páginas añadidas en el periodo', { html: String(a.paginasCreadas ?? '—') }],
        ],
      ),
    );
  }
  if (r.plantillaEnsayos) {
    const p = r.plantillaEnsayos;
    bits.push(sectionTitle('Plantilla en ensayos'));
    bits.push(
      simpleTable(
        ['Indicador', 'Valor'],
        [
          ['Músicos activos', { html: String(p.musicosActivos ?? '—') }],
          [
            'Con al menos un «Presente» en el rango',
            { html: String(p.musicosConPresencia ?? '—') },
          ],
          [
            'Ratio con presencia',
            { html: p.ratioPresencia != null ? formatPct(p.ratioPresencia) : '—' },
          ],
        ],
      ),
    );
  }
  return bits.join('');
}

function buildPulidoFase7(p7, rb, withCharts, t) {
  if (!p7) return '';
  const out = [];
  out.push(sectionTitle(t.fase7.cardTitle));
  if (withCharts) {
    if ((p7.sedesMasUsadas || []).length) {
      out.push(
        `<div class="ic" style="margin-top:0"><div class="ic-label">${esc(t.fase7.sedesTitle)}</div>${hBarsFromNamed(p7.sedesMasUsadas, 'nombre', 'ensayos', 15)}</div>`,
      );
    } else {
      out.push(
        `<p style="font-size:12px;color:#718096">${esc(t.fase7.emptySedes)}</p>`,
      );
    }
    if ((p7.gruposMasUsados || []).length) {
      out.push(
        `<div class="ic" style="margin-top:8px"><div class="ic-label">${esc(t.fase7.gruposTitle)}</div>${hBarsFromNamed(p7.gruposMasUsados, 'nombre', 'ensayos', 15)}</div>`,
      );
    } else {
      out.push(
        `<p style="font-size:12px;color:#718096;margin-top:8px">${esc(t.fase7.emptyGrupos)}</p>`,
      );
    }
  } else {
    out.push(
      simpleTable(
        [t.fase7.sedesTitle, 'Ensayos'],
        (p7.sedesMasUsadas || []).map((s) => [s.nombre, { html: String(s.ensayos), numeric: true }]),
      ),
    );
    if (!(p7.sedesMasUsadas || []).length) {
      out.push(`<p style="font-size:12px;color:#718096">${esc(t.fase7.emptySedes)}</p>`);
    }
    out.push(
      simpleTable(
        [t.fase7.gruposTitle, 'Ensayos'],
        (p7.gruposMasUsados || []).map((g) => [g.nombre, { html: String(g.ensayos), numeric: true }]),
      ),
    );
    if (!(p7.gruposMasUsados || []).length) {
      out.push(
        `<p style="font-size:12px;color:#718096">${esc(t.fase7.emptyGrupos)}</p>`,
      );
    }
  }
  const av = p7.avalesCreadosEnRango;
  if (av) {
    out.push(
      `<h3 class="minih3" style="margin:14px 0 6px">${esc(t.fase7.avalesTitle)}</h3>`,
    );
    if (av.creadosEnRango > 0) {
      const items = toPieDataWithKeyColors(av.porStatus || {}, LABEL_AVAL_STATUS, HEX_AVAL);
      if (withCharts) {
        out.push(
          `<div class="ic"><div class="ic-label">Por estado</div>${svgPieFromItems(
            items.map((x) => ({ name: x.name, value: x.value, color: x.color })),
          )}</div>`,
        );
      } else {
        out.push(
          simpleTable(
            ['Estado', 'Nº'],
            Object.entries(av.porStatus || {})
              .filter(([, n]) => n > 0)
              .map(([k, n]) => [LABEL_AVAL_STATUS[k] || k, { html: String(n), numeric: true }]),
          ),
        );
      }
    } else {
      out.push(
        `<p style="font-size:12px;color:#718096">${esc(t.fase7.avalesEmpty)}</p>`,
      );
    }
  }
  if (rb) {
    out.push(
      `<h3 class="minih3" style="margin:12px 0 6px">${esc(t.fase7.resenasTitle)}</h3>`,
    );
    out.push(
      simpleTable(
        ['Dato', 'Valor'],
        [
          ['Total recibidas', { html: String(rb.totalRecibidas ?? '—') }],
          ['Publicadas', { html: String(rb.publicadas ?? '—') }],
          ['No publicadas', { html: String(rb.sinPublicar ?? '—') }],
          [
            'Comunicación (media 1–5)',
            { html: rb.promedios?.comunicacion != null ? String(rb.promedios.comunicacion) : '—' },
          ],
          [
            'General (media 1–5)',
            { html: rb.promedios?.general != null ? String(rb.promedios.general) : '—' },
          ],
          [
            'Puntualidad (media 1–5)',
            { html: rb.promedios?.puntualidad != null ? String(rb.promedios.puntualidad) : '—' },
          ],
          [
            'Calidad artística (media 1–5)',
            { html: rb.promedios?.calidadArtistica != null ? String(rb.promedios.calidadArtistica) : '—' },
          ],
        ],
      ),
    );
  }
  return out.join('');
}

function buildComparativa(c, t) {
  if (!c) return '';
  const ra = c.rango;
  const sub = `Referencia: ${formatYmdEs(ra?.desdeYmd)} — ${formatYmdEs(ra?.hastaYmd)} (misma longitud, hacia el pasado).`;
  const rows = Object.values(c.kpi || {}).map((d) => [
    LABEL_DELTA_KPI[d.clave] || d.clave,
    { html: String(d.anterior), numeric: true },
    { html: String(d.actual), numeric: true },
    { html: formatDeltaLine(d), numeric: true },
  ]);
  return (
    sectionTitle(t.informe.comparativaTitulo) +
    `<p style="font-size:11px;margin-bottom:8px;color:#4A5568">${esc(sub)}</p>` +
    simpleTable(['Indicador', 'Anterior', 'Actual', 'Variación'], rows) +
    (c.tasaCerrado?.anterior != null || c.tasaCerrado?.actual != null
      ? `<p style="font-size:11px;margin-top:8px">Tasa presente/cerrado (asist. ensayos): anterior ${formatPct(c.tasaCerrado?.anterior)} · actual ${formatPct(c.tasaCerrado?.actual)}${
        c.tasaCerrado?.diffPuntos != null
          ? ` (Δ ${(c.tasaCerrado.diffPuntos * 100).toFixed(1)} p. p.)`
          : ''
      }</p>`
      : '')
  );
}

function buildEvolucion(d) {
  const r = d.resumen;
  if (!r || !Array.isArray(r.serieMensual)) return '';
  const s = r.serieMensual;
  const out = [sectionTitle('Evolución y ranking (mes civil)')];
  out.push(
    simpleTable(
      ['Mes', 'Ensayos', 'Actuaciones'],
      s.map((fila) => [
        String(fila.label || ''),
        { html: String(fila.ensayos), numeric: true },
        { html: String(fila.actuaciones), numeric: true },
      ]),
    ),
  );
  out.push(
    '<h3 class="minih3" style="margin:12px 0 6px">Ranking presentes a ensayos</h3>',
  );
  if ((r.rankingPresentesEnsayos || []).length) {
    out.push(
      simpleTable(
        ['#', 'Músico', 'Presente'],
        r.rankingPresentesEnsayos.map((m) => [
          m.posicion,
          nombreMusico(m.nombre, m.apellidos),
          { html: String(m.presentes), numeric: true },
        ]),
      ),
    );
  } else {
    out.push(`<p style="font-size:12px">No hay presentes en el periodo.</p>`);
  }
  const rank = r.pulidoFase7?.rankingTasaAsistenciaEnsayos;
  if (rank && rank.length) {
    out.push(
      simpleTable(
        ['#', 'Músico', '% asist.', 'Presente', 'Conv.'],
        rank.map((m) => [
          m.posicion,
          nombreMusico(m.nombre, m.apellidos),
          { html: formatPct(m.tasa), numeric: true },
          { html: String(m.presentes), numeric: true },
          { html: String(m.convocatorias), numeric: true },
        ]),
      ),
    );
  } else {
    out.push(
      '<p style="font-size:11px;color:#718096;margin-top:6px">Ranking por % de asistencia: sin datos o sin cumplir el mínimo de convocatorias.</p>',
    );
  }
  return out.join('');
}

/**
 * @param {{ data: object, modoInforme: object|null, withCharts: boolean, t: object }} params
 */
export async function openMetricasPdfWindow({ data, modoInforme, withCharts, t }) {
  const w = window.open('', '_blank');
  if (!w) {
    const err = new Error('NO_POPUP');
    err.code = 'NO_POPUP';
    throw err;
  }

  const origin = window.location.origin;
  const [logoDataUrl, musicoDataUrl] = await Promise.all([
    fetchImgDataUrl(origin, '/logo%20y%20texto%20cadencya%20blanco.png'),
    fetchImgDataUrl(origin, '/musico-cofradia-1.png'),
  ]);

  const IMG_LOGO = logoDataUrl
    ? `<img src="${logoDataUrl}" alt="Cadencya" style="height:42px;display:block">`
    : `<span style="font-size:21px;font-weight:900;color:#fff;letter-spacing:-0.5px">Cadencya</span>`;
  const IMG_LOGO_SM = logoDataUrl
    ? `<img src="${logoDataUrl}" alt="Cadencya" style="height:28px;display:block">`
    : `<span style="font-size:14px;font-weight:900;color:#fff">Cadencya</span>`;
  const IMG_MUSICO = (h = 70, o = 0.28) =>
    musicoDataUrl
      ? `<img src="${musicoDataUrl}" alt="" style="height:${h}px;opacity:${o};display:block;object-fit:contain">`
      : '';

  const { linea1, linea2 } = tituloPeriodo(modoInforme, data, t);
  const vistaStr = withCharts
    ? t.informe.pdfVistaGraficosLabel
    : t.informe.pdfVistaDetalleLabel;
  const now = new Date().toLocaleString('es-ES', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const r = data.resumen;
  const narr = data.narrativaIa
    ? `${sectionTitle('Lectura del periodo (IA)')}
       <div class="obs-box" style="white-space:pre-wrap;font-size:12px;margin-bottom:12px">${esc(
         data.narrativaIa,
       )}</div>`
    : '';

  const main = r ? buildResumenMain(data, withCharts) : '';
  const rep = r?.repertorioActuaciones
    ? buildRepertorio(r.repertorioActuaciones, withCharts)
    : '';
  const enc = r ? buildEncuestasActuacion(r, withCharts) : '';
  const ap = r ? buildArchivoPlantilla(r) : '';
  const f7 = r?.pulidoFase7 ? buildPulidoFase7(r.pulidoFase7, r.resenasBanda, withCharts, t) : '';
  const comp = data.comparativaAnterior ? buildComparativa(data.comparativaAnterior, t) : '';
  const evo = r ? buildEvolucion(data) : '';

  w.document.write(`<!DOCTYPE html>
<html lang="es"><head>
<meta charset="utf-8">
<title>${esc(linea1)} — ${esc(t.informe.pdfTitleShort)}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Segoe UI',system-ui,sans-serif;color:#1A1A2E;background:#fff;font-size:13px;line-height:1.55}
.hd{background:linear-gradient(135deg,#2D1B69 0%,#6B46C1 55%,#9F7AEA 100%);display:flex;align-items:center;justify-content:space-between;padding:22px 32px;gap:16px;position:relative;overflow:hidden}
.hd::after{content:'';position:absolute;right:90px;bottom:-30px;width:120px;height:120px;background:rgba(255,255,255,0.04);border-radius:50%}
.hd-logo{display:flex;align-items:center;flex-shrink:0}
.hd-center{flex:1;text-align:center;padding:0 16px}
.hd-label{font-size:9px;color:#C4B5FD;letter-spacing:2px;text-transform:uppercase;margin-bottom:5px}
.hd-name{font-size:19px;font-weight:800;color:#fff;line-height:1.25}
.hd-tipo{font-size:12px;color:#E9D8FD;margin-top:3px}
.hd-deco{flex-shrink:0;opacity:0.28}
.hd-stripe{height:4px;background:linear-gradient(90deg,#553C9A,#B794F4,#6B46C1)}
.badge-line{padding:10px 32px 0;font-size:11px}
.badge{display:inline-block;padding:3px 10px;border-radius:20px;font-size:10px;font-weight:700;background:#EDE9FE;color:#553C9A}
.minih3{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:#6B46C1}
.content{padding:20px 32px 20px}
.section-title{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.2px;color:#6B46C1;border-bottom:1.5px solid #E9D8FD;padding-bottom:5px;margin:16px 0 10px}
.section-title:first-of-type{margin-top:0}
.ic{background:#FAF5FF;border:1.5px solid #E9D8FD;border-radius:10px;padding:10px 12px;margin-bottom:10px}
.ic-label{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;color:#805AD5;margin-bottom:6px}
table{width:100%;border-collapse:collapse;margin-top:8px;font-size:12px}
thead tr{background:linear-gradient(135deg,#553C9A,#7C3AED);color:#fff}
thead th{padding:7px 10px;text-align:left;font-weight:600;font-size:11px;letter-spacing:0.3px}
tbody tr:nth-child(even){background:#FAF5FF}
tbody td{padding:6px 10px;border-bottom:1px solid #EDE9FE;color:#2D1B69;vertical-align:top}
.obs-box{background:#EEF2FF;border-left:3px solid #6B46C1;border-radius:0 8px 8px 0;padding:10px 14px}
.print-grid-2{page-break-inside:avoid}
@media (max-width: 640px){.print-grid-2{grid-template-columns:1fr !important}}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
.footer{background:linear-gradient(135deg,#1A0D40 0%,#2D1B69 100%);padding:11px 32px;display:flex;align-items:center;justify-content:space-between;margin-top:24px;page-break-inside:avoid}
.footer-brand{display:flex;flex-direction:column;align-items:flex-start;gap:4px}
.footer-tagline{font-size:10px;color:#B794F4}
.footer-meta{font-size:10px;color:#9F7AEA;text-align:right}
.footer-deco{opacity:0.35;flex-shrink:0}
</style>
</head>
<body>
<div class="hd">
  <div class="hd-logo">${IMG_LOGO}</div>
  <div class="hd-center">
    <div class="hd-label">${esc(t.informe.pdfHeaderLabel)}</div>
    <div class="hd-name">${esc(linea1)}</div>
    ${linea2 ? `<div class="hd-tipo">${esc(linea2)}</div>` : ''}
  </div>
  <div class="hd-deco">${IMG_MUSICO(70, 0.32)}</div>
</div>
<div class="hd-stripe"></div>
<div class="badge-line"><span class="badge">${esc(vistaStr)}</span></div>
<div class="content">
  ${narr}
  ${main}
  ${rep}
  ${enc}
  ${ap}
  ${f7}
  ${comp}
  ${evo}
</div>
<div class="footer">
  <div class="footer-brand">
    ${IMG_LOGO_SM}
    <div class="footer-tagline" style="margin-top:4px">La herramienta de tu banda</div>
  </div>
  <div class="footer-meta">Generado el ${esc(now)}<br>cadencya.app</div>
  <div class="footer-deco">${IMG_MUSICO(36, 0.4)}</div>
</div>
</body></html>`);
  w.document.close();
  setTimeout(() => {
    try {
      w.focus();
      w.print();
    } catch (e) {
      console.error(e);
    }
  }, 400);
}
